import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { trackCalculatorConversion } from '../../lib/gtag';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Calculator, CheckCircle, Send, Ruler } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const BuyoutCalculatorModal = ({ open, onClose }) => {
  const [amount, setAmount] = useState('');
  const [productLink, setProductLink] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [insurance, setInsurance] = useState(false);
  const [photoReport, setPhotoReport] = useState(false);

  const calculate = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error('Введіть суму в юанях'); return; }
    if (!productLink.trim()) { toast.error('Спочатку заповніть посилання на товар'); return; }
    if (!email.trim()) { toast.error('Спочатку заповніть email'); return; }
    if (!phone.trim()) { toast.error('Спочатку заповніть номер телефону'); return; }
    setLoading(true);
    setSent(false);
    try {
      const response = await axios.post(`${API}/calculator`, { amount_yuan: amt, notify: false });
      setResult(response.data);
    } catch (error) {
      toast.error('Помилка розрахунку');
    } finally {
      setLoading(false);
    }
  };

  const sendToManager = async () => {
    if (!result) return;
    if (!phone) { toast.error('Введіть номер телефону'); return; }
    if (!email) { toast.error('Введіть email'); return; }
    if (!productLink) { toast.error('Введіть посилання на товар'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/guest-request`, {
        amount_yuan: result.amount_yuan, product_link: productLink, phone, email,
        insurance, photo_report: photoReport
      });
      toast.success("Заявку відправлено! Менеджер зв'яжеться з вами.", { duration: 6000 });
      setSent(true);
    } catch (error) {
      toast.error('Помилка відправки заявки');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto bg-[#0a1224]/95 backdrop-blur-xl border border-blue-500/40 text-white shadow-[0_0_60px_-15px_rgba(59,130,246,0.7)]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl text-white">
            <span className="w-9 h-9 bg-gradient-to-br from-cyan-400 to-blue-600 rounded-lg flex items-center justify-center shadow-[0_0_15px_rgba(6,182,212,0.5)]">
              <Calculator className="w-5 h-5 text-white" />
            </span>
            Розрахунок вартості викупу
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Сума в юанях (CNY) <span className="text-red-500">*</span></label>
            <Input type="number" placeholder="Введіть суму" value={amount}
              onChange={e => { setAmount(e.target.value); setResult(null); }} className="h-11 bg-slate-900/60 border-blue-500/30 text-white placeholder:text-slate-500 focus:border-cyan-400" data-testid="buyout-calc-amount" />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">Посилання на товар <span className="text-red-500">*</span></label>
            <Input type="url" placeholder="https://..." value={productLink}
              onChange={e => setProductLink(e.target.value)} className="h-10 bg-slate-900/60 border-blue-500/30 text-white placeholder:text-slate-500 focus:border-cyan-400" data-testid="buyout-calc-link" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">Email <span className="text-red-500">*</span></label>
            <Input type="email" placeholder="example@mail.com" value={email}
              onChange={e => setEmail(e.target.value)} className="h-10 bg-slate-900/60 border-blue-500/30 text-white placeholder:text-slate-500 focus:border-cyan-400" data-testid="buyout-calc-email" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">Номер телефону <span className="text-red-500">*</span></label>
            <Input type="tel" placeholder="+380..." value={phone}
              onChange={e => setPhone(e.target.value)} className="h-10 bg-slate-900/60 border-blue-500/30 text-white placeholder:text-slate-500 focus:border-cyan-400" data-testid="buyout-calc-phone" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <label className={`flex items-center gap-2.5 rounded-lg border px-3 py-2.5 cursor-pointer transition-all ${insurance ? 'border-cyan-400/70 bg-cyan-500/10 shadow-[0_0_12px_rgba(6,182,212,0.25)]' : 'border-blue-500/30 bg-slate-900/60 hover:border-blue-400/50'}`} data-testid="buyout-insurance-label">
              <input type="checkbox" checked={insurance} onChange={e => setInsurance(e.target.checked)} className="w-4 h-4 accent-cyan-400" data-testid="buyout-insurance-checkbox" />
              <span className="text-sm text-slate-200">🛡 Страхування <span className="text-cyan-300 font-semibold">3%</span></span>
            </label>
            <label className={`flex items-center gap-2.5 rounded-lg border px-3 py-2.5 cursor-pointer transition-all ${photoReport ? 'border-cyan-400/70 bg-cyan-500/10 shadow-[0_0_12px_rgba(6,182,212,0.25)]' : 'border-blue-500/30 bg-slate-900/60 hover:border-blue-400/50'}`} data-testid="buyout-photo-label">
              <input type="checkbox" checked={photoReport} onChange={e => setPhotoReport(e.target.checked)} className="w-4 h-4 accent-cyan-400" data-testid="buyout-photo-checkbox" />
              <span className="text-sm text-slate-200">📷 Фотозвіт <span className="text-cyan-300 font-semibold">+$3</span></span>
            </label>
          </div>

          <Button onClick={calculate} disabled={loading} className="w-full h-12 gap-2 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-bold shadow-[0_0_20px_rgba(6,182,212,0.4)] hover:shadow-[0_0_30px_rgba(6,182,212,0.7)] transition-all" data-testid="buyout-calc-submit">
            <Calculator className="w-4 h-4" /> {loading ? 'Розрахунок...' : 'Розрахувати вартість'}
          </Button>

          {result && (
            <div className="bg-slate-900/60 border border-blue-500/20 rounded-xl p-5 space-y-3 animate-fadeIn" data-testid="buyout-calc-result">
              <div className="flex justify-between py-2 border-b border-slate-700/60 text-sm">
                <span className="text-slate-400">Сума в юанях:</span>
                <span className="font-semibold text-white">{result.amount_yuan.toFixed(2)} CNY</span>
              </div>
              <div className="flex justify-between py-2 border-b border-slate-700/60 text-sm">
                <span className="text-slate-400">Конвертація:</span>
                <span className="font-semibold text-white">{result.amount_usd.toFixed(2)} USD</span>
              </div>
              <div className="flex justify-between py-2 border-b border-slate-700/60 text-sm">
                <span className="text-slate-400">Комісія:</span>
                <span className="font-semibold text-white">+{result.commission_usd.toFixed(2)} USD</span>
              </div>
              <div className="flex justify-between py-2 border-b border-slate-700/60 text-sm">
                <span className="text-slate-400">Підсумок USD:</span>
                <span className="font-semibold text-white">{result.total_usd.toFixed(2)} USD</span>
              </div>
              {insurance && (
                <div className="flex justify-between py-2 border-b border-slate-700/60 text-sm" data-testid="buyout-insurance-line">
                  <span className="text-slate-400">🛡 Страхування (3%):</span>
                  <span className="font-semibold text-cyan-300">+{(result.total_usd * 0.03).toFixed(2)} USD</span>
                </div>
              )}
              {photoReport && (
                <div className="flex justify-between py-2 border-b border-slate-700/60 text-sm" data-testid="buyout-photo-line">
                  <span className="text-slate-400">📷 Фотозвіт:</span>
                  <span className="font-semibold text-cyan-300">+3.00 USD</span>
                </div>
              )}
              <div className="flex justify-between items-center py-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg px-4 shadow-[0_0_20px_rgba(6,182,212,0.4)]">
                <span className="font-semibold">До оплати:</span>
                <span className="text-2xl font-bold">
                  {(result.final_uah + (((insurance ? result.total_usd * 0.03 : 0) + (photoReport ? 3 : 0)) * (result.final_uah / result.total_usd))).toFixed(2)} грн
                </span>
              </div>

              {sent ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-center gap-2 p-3 bg-emerald-500/15 border border-emerald-400/30 rounded-lg text-emerald-300">
                    <CheckCircle className="w-4 h-4" />
                    <span className="text-sm font-medium">Заявку відправлено! Менеджер зв'яжеться з вами.</span>
                  </div>
                  <div className="p-3.5 bg-blue-500/10 border border-blue-400/30 rounded-lg text-sm text-slate-300 leading-relaxed" data-testid="buyout-approx-note">
                    ℹ️ Це <b className="text-white">орієнтовна вартість</b>. Повну вартість вам повідомить менеджер — очікуйте дзвінка. А поки можете{' '}
                    <a href="/register" className="text-cyan-300 font-semibold underline hover:text-cyan-200" data-testid="buyout-register-link">зареєструватися</a>
                    {' '}на сайті — так ваша заявка буде одразу у вашому кабінеті.
                  </div>
                </div>
              ) : (
                <Button onClick={sendToManager} disabled={loading} className="w-full h-11 gap-2 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-bold shadow-[0_0_20px_rgba(6,182,212,0.4)] hover:shadow-[0_0_30px_rgba(6,182,212,0.7)] transition-all" data-testid="buyout-calc-send">
                  <Send className="w-4 h-4" /> Відправити менеджеру
                </Button>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const ShippingCalculatorModal = ({ open, onClose }) => {
  const [length, setLength] = useState('');
  const [width, setWidth] = useState('');
  const [height, setHeight] = useState('');
  const [actualWeight, setActualWeight] = useState('');
  const [productLink, setProductLink] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [captcha, setCaptcha] = useState(null);
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadCaptcha = async () => {
    try {
      const res = await axios.get(`${API}/captcha/generate`);
      setCaptcha(res.data);
      setCaptchaAnswer('');
    } catch (e) {}
  };

  useEffect(() => {
    if (open) loadCaptcha();
  }, [open]);

  const calculate = async () => {
    if ((!length || !width || !height) && !actualWeight) {
      toast.error('Введіть розміри або фактичну вагу');
      return;
    }
    if (!productLink.trim()) {
      toast.error('Посилання на товар обов\'язкове');
      return;
    }
    if (!email.trim()) {
      toast.error('Введіть email');
      return;
    }
    if (!captchaAnswer) {
      toast.error('Введіть відповідь на капчу');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/shipping-calculator`, {
        length: parseFloat(length) || 0,
        width: parseFloat(width) || 0,
        height: parseFloat(height) || 0,
        actual_weight: parseFloat(actualWeight) || 0,
        product_link: productLink.trim(),
        email: email.trim(), phone, name,
        captcha_token: captcha?.captcha_token,
        captcha_answer: parseInt(captchaAnswer)
      });
      trackCalculatorConversion();
      setResult(res.data);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Помилка розрахунку';
      toast.error(msg);
      loadCaptcha();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto bg-[#0a1224]/95 backdrop-blur-xl border border-blue-500/40 text-white shadow-[0_0_60px_-15px_rgba(59,130,246,0.7)]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl text-white">
            <span className="w-9 h-9 bg-gradient-to-br from-cyan-400 to-blue-600 rounded-lg flex items-center justify-center shadow-[0_0_15px_rgba(6,182,212,0.5)]">
              <Ruler className="w-5 h-5 text-white" />
            </span>
            Розрахунок вартості доставки
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          {/* Dimensions */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Розміри вантажу (в метрах)
            </label>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="text-xs text-slate-400">Довжина (м)</label>
                <Input type="number" step="0.01" placeholder="0.50" value={length}
                  onChange={e => setLength(e.target.value)} className="h-10 bg-slate-900/60 border-blue-500/30 text-white placeholder:text-slate-500 focus:border-cyan-400" data-testid="ship-calc-length" />
              </div>
              <div>
                <label className="text-xs text-slate-400">Ширина (м)</label>
                <Input type="number" step="0.01" placeholder="0.40" value={width}
                  onChange={e => setWidth(e.target.value)} className="h-10 bg-slate-900/60 border-blue-500/30 text-white placeholder:text-slate-500 focus:border-cyan-400" data-testid="ship-calc-width" />
              </div>
              <div>
                <label className="text-xs text-slate-400">Висота (м)</label>
                <Input type="number" step="0.01" placeholder="0.50" value={height}
                  onChange={e => setHeight(e.target.value)} className="h-10 bg-slate-900/60 border-blue-500/30 text-white placeholder:text-slate-500 focus:border-cyan-400" data-testid="ship-calc-height" />
              </div>
            </div>
            {length && width && height && (
              <p className="text-xs text-cyan-400 mt-1">
                Об'ємна вага: {(parseFloat(length || 0) * parseFloat(width || 0) * parseFloat(height || 0) * 167).toFixed(1)} кг
                <span className="text-slate-400 ml-1">({length} × {width} × {height} × 167)</span>
              </p>
            )}
          </div>

          {/* Actual weight */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">
              Фактична вага (кг)
            </label>
            <Input type="number" step="0.1" placeholder="Наприклад: 20" value={actualWeight}
              onChange={e => setActualWeight(e.target.value)} className="h-10 bg-slate-900/60 border-blue-500/30 text-white placeholder:text-slate-500 focus:border-cyan-400" data-testid="ship-calc-weight" />
            <p className="text-xs text-slate-500 mt-1">Тариф розраховується по більшій вазі (об'ємній або фактичній)</p>
          </div>

          {/* Product link */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">
              Посилання на товар <span className="text-red-500">*</span>
            </label>
            <Input type="url" placeholder="https://..." value={productLink}
              onChange={e => setProductLink(e.target.value)} className="h-10 bg-slate-900/60 border-blue-500/30 text-white placeholder:text-slate-500 focus:border-cyan-400" data-testid="ship-calc-link" />
          </div>

          {/* Contact info */}
          <div className="border-t border-slate-700/60 pt-4">
            <label className="block text-sm font-medium text-slate-300 mb-2">Контактні дані</label>
            <div className="space-y-3">
              <Input type="text" placeholder="Ваше ім'я" value={name}
                onChange={e => setName(e.target.value)} className="h-10 bg-slate-900/60 border-blue-500/30 text-white placeholder:text-slate-500 focus:border-cyan-400" data-testid="ship-calc-name" />
              <div>
                <Input type="email" placeholder="Email *" value={email}
                  onChange={e => setEmail(e.target.value)} className="h-10 bg-slate-900/60 border-blue-500/30 text-white placeholder:text-slate-500 focus:border-cyan-400" data-testid="ship-calc-email" />
                <p className="text-xs text-slate-500 mt-1">Один email — один розрахунок</p>
              </div>
              <Input type="tel" placeholder="+380..." value={phone}
                onChange={e => setPhone(e.target.value)} className="h-10 bg-slate-900/60 border-blue-500/30 text-white placeholder:text-slate-500 focus:border-cyan-400" data-testid="ship-calc-phone" />
            </div>
          </div>

          {/* Captcha */}
          {captcha && (
            <div className="border-t border-slate-700/60 pt-4">
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Перевірка: {captcha.question} = ?
              </label>
              <div className="flex gap-2">
                <Input type="number" placeholder="Відповідь" value={captchaAnswer}
                  onChange={e => setCaptchaAnswer(e.target.value)} className="h-10 w-32 bg-slate-900/60 border-blue-500/30 text-white placeholder:text-slate-500 focus:border-cyan-400"
                  data-testid="ship-calc-captcha" />
                <Button variant="ghost" size="sm" onClick={loadCaptcha} className="h-10 text-xs text-slate-400 hover:text-white hover:bg-slate-800/60">
                  Інше питання
                </Button>
              </div>
            </div>
          )}

          {/* Calculate button */}
          <Button onClick={calculate} disabled={loading} className="w-full h-11 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-bold shadow-[0_0_20px_rgba(6,182,212,0.4)] hover:shadow-[0_0_30px_rgba(6,182,212,0.7)] transition-all" data-testid="ship-calc-submit">
            {loading ? 'Розрахунок...' : 'Розрахувати'}
          </Button>

          {/* Result */}
          {result && (
            <div className="bg-slate-900/60 border border-blue-500/20 rounded-xl p-5 space-y-3 animate-fadeIn" data-testid="ship-calc-result">
              <div className="grid grid-cols-2 gap-3 text-sm">
                {result.volume_weight > 0 && (
                  <div className="bg-slate-800/70 border border-slate-700/60 rounded-lg p-3">
                    <p className="text-slate-400 text-xs">Об'ємна вага</p>
                    <p className="font-bold text-lg text-white">{result.volume_weight} кг</p>
                  </div>
                )}
                {result.actual_weight > 0 && (
                  <div className="bg-slate-800/70 border border-slate-700/60 rounded-lg p-3">
                    <p className="text-slate-400 text-xs">Фактична вага</p>
                    <p className="font-bold text-lg text-white">{result.actual_weight} кг</p>
                  </div>
                )}
                <div className="bg-slate-800/70 border border-slate-700/60 rounded-lg p-3">
                  <p className="text-slate-400 text-xs">Розрахункова вага</p>
                  <p className="font-bold text-lg text-cyan-400">{result.billable_weight} кг</p>
                </div>
                <div className="bg-slate-800/70 border border-slate-700/60 rounded-lg p-3">
                  <p className="text-slate-400 text-xs">Тариф</p>
                  <p className="font-bold text-lg text-white">${result.price_per_kg}/кг</p>
                </div>
              </div>
              <div className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg p-4 text-center shadow-[0_0_20px_rgba(6,182,212,0.4)]">
                <p className="text-sm opacity-80">Орієнтовна вартість доставки</p>
                <p className="text-3xl font-bold">${result.total_usd}</p>
              </div>
              <div className="flex items-center justify-center gap-2 p-3 bg-emerald-500/15 border border-emerald-400/30 rounded-lg text-emerald-300">
                <CheckCircle className="w-4 h-4" />
                <span className="text-sm font-medium">Заявку відправлено! Менеджер зв'яжеться з вами.</span>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
